import type React from "react"
import { Component, type ComponentType, type ReactNode } from "react"

interface ErrorBoundaryProps {
  children: ReactNode
  fallback: ReactNode
  componentName: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary to catch component rendering errors
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      `Error in component ${this.props.componentName}:`,
      error,
      errorInfo,
    )
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }

    return this.props.children
  }
}

export interface ComponentShowcaseProps {
  /** The component to render */
  component: ComponentType<Record<string, unknown>>
  /** Display name for the component */
  name: string
  /** Component ID for keys */
  id: string
  /** Optional description */
  description?: string
  /** Props to pass to the component */
  demoProps?: Record<string, unknown>
  /** Additional className for the wrapper */
  className?: string
}

/**
 * ComponentShowcase
 *
 * Wraps a component with error boundaries and displays it with metadata.
 * Handles component rendering errors gracefully.
 */
export function ComponentShowcase({
  component: ComponentToRender,
  name,
  id,
  description,
  demoProps = {},
  className = "",
}: ComponentShowcaseProps) {
  return (
    <div className={`space-y-3 ${className}`} data-component-id={id}>
      {/* Component header */}
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-foreground text-sm font-semibold">{name}</h4>
          {description && (
            <p className="text-muted-foreground mt-1 text-xs">{description}</p>
          )}
        </div>
        <code className="bg-muted text-muted-foreground rounded px-2 py-1 text-xs">
          {id}
        </code>
      </div>

      {/* Component preview with error boundary */}
      <div className="bg-card overflow-hidden rounded-lg border-2 border-dashed border-gray-300 p-8 dark:border-gray-700">
        <ErrorBoundary
          componentName={name}
          fallback={
            <div className="p-8 text-center">
              <div className="bg-destructive/10 text-destructive inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm">
                <svg
                  className="size-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span>Failed to render &quot;{name}&quot;</span>
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                Check console for details
              </p>
            </div>
          }
        >
          <ComponentToRender {...demoProps} />
        </ErrorBoundary>
      </div>
    </div>
  )
}
